import {Alert, Button, Input, Modal, Spin} from "antd";
import {FormEvent, useEffect, useState} from "react";
import styled from "styled-components";

export type UnsplashCoverImage = {
  url: string;
  alt: string;
  photographerName: string;
  photographerUrl: string;
};

type UnsplashPhoto = {
  id: string;
  alt_description: string | null;
  urls: {small: string; regular: string};
  links: {download_location: string};
  user: {name: string; links: {html: string}};
};

const PickerBody = styled.div`
  .search-row {
    display: flex;
    gap: 0.8rem;
    margin-bottom: 1.6rem;
  }

  .search-row .ant-input-affix-wrapper {
    min-height: 4rem;
  }

  .results {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.2rem;
    max-height: 52rem;
    overflow-y: auto;
  }

  .photo {
    overflow: hidden;
    padding: 0;
    border: 1px solid #ebebeb;
    border-radius: 0.8rem;
    background: #ffffff;
    text-align: left;
    cursor: pointer;
  }

  .photo img {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
  }

  .photo span {
    display: block;
    overflow: hidden;
    padding: 0.8rem;
    color: #555555;
    font-size: 1.1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loading {
    display: grid;
    min-height: 20rem;
    place-items: center;
  }

  @media (max-width: 600px) {
    .results {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;

const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY?.trim();
const referralQuery = "utm_source=coachli&utm_medium=referral";

function withReferral(url: string) {
  return `${url}${url.includes("?") ? "&" : "?"}${referralQuery}`;
}

export function UnsplashPicker({
  visible,
  onClose,
  onSelect,
}: {
  visible: boolean;
  onClose: () => void;
  onSelect: (image: UnsplashCoverImage) => void;
}) {
  const [query, setQuery] = useState("nature landscape");
  const [photos, setPhotos] = useState<UnsplashPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (searchQuery: string) => {
    if (!accessKey) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        per_page: "18",
        orientation: "landscape",
        content_filter: "high",
      });
      const response = await fetch(`https://api.unsplash.com/search/photos?${params}`, {
        headers: {Authorization: `Client-ID ${accessKey}`, "Accept-Version": "v1"},
      });
      if (!response.ok) throw new Error("Unable to load Unsplash images");
      const data = (await response.json()) as {results: UnsplashPhoto[]};
      setPhotos(data.results);
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to load images"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && accessKey && photos.length === 0) void search(query);
    // The initial search should only run when the picker opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (query.trim()) void search(query.trim());
  };

  const choosePhoto = (photo: UnsplashPhoto) => {
    void fetch(photo.links.download_location, {
      headers: {Authorization: `Client-ID ${accessKey}`, "Accept-Version": "v1"},
    }).catch(() => undefined);
    onSelect({
      url: photo.urls.regular,
      alt: photo.alt_description ?? "",
      photographerName: photo.user.name,
      photographerUrl: withReferral(photo.user.links.html),
    });
  };

  return (
    <Modal
      visible={visible}
      title="Choose a cover image"
      footer={null}
      width={820}
      destroyOnClose
      onCancel={onClose}
    >
      <PickerBody>
        {!accessKey ? (
          <Alert
            type="warning"
            showIcon
            message="Unsplash is not configured"
            description="Add REACT_APP_UNSPLASH_ACCESS_KEY to the application environment."
          />
        ) : (
          <>
            <form className="search-row" onSubmit={handleSubmit}>
              <Input
                value={query}
                aria-label="Search Unsplash"
                placeholder="Search high-resolution images"
                onChange={(event) => setQuery(event.target.value)}
              />
              <Button type="primary" htmlType="submit" loading={loading}>
                Search
              </Button>
            </form>
            {error ? <Alert type="error" showIcon message={error} /> : null}
            {loading ? (
              <div className="loading">
                <Spin />
              </div>
            ) : (
              <div className="results">
                {photos.map((photo) => (
                  <button
                    type="button"
                    className="photo"
                    key={photo.id}
                    onClick={() => choosePhoto(photo)}
                  >
                    <img src={photo.urls.small} alt={photo.alt_description ?? ""} />
                    <span>Photo by {photo.user.name}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </PickerBody>
    </Modal>
  );
}
